import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { useEffect } from "react";
import AppLayout from "./layouts/app-layout";
import ProtectedRoute from "./components/protected-route";
import { ThemeProvider } from "./components/theme-provider";
import LandingPage from "./pages/landing";
import DogAnalyzer from "./pages/DogAnalyzer";
import ErrorBoundary from "./pages/error-boundary";
import { AnalysisProvider } from './AnalysisContext';
import OverviewTab from "./pages/OverviewTab";
import HealthTab from "./pages/HealthTab";
import CareTab from "./pages/CareTab";
import BreedCharacteristicsTab from "./pages/BreedCharacteristicsTab";
import NutritionalTab from "./pages/NutritionalTab";
import ReportHistorySection from "./pages/ReportHistorySection";
import TipsGuide from "./pages/TipsGuide";
import AnalysisProtectedRoute from "./AnalysisProtectedRoute";
import Success from "./pages/success";
import Failure from "./pages/failure";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DataDeletion from "./pages/DataDeletion";
import { trackPageView as trackPixelPageView } from './utils/pixelService.js';
import { trackPageView as trackGaPageView } from './utils/gaService.js';

// Create a function to track route changes
const trackRouteChange = () => {
  // Track page view with current URL path as the page name
  const pageData = {
    page_path: window.location.pathname,
    page_title: document.title
  };
  
  // Track in Facebook Pixel
  trackPixelPageView(pageData);
  
  // Track in Google Analytics
  trackGaPageView(pageData);
};

const router = createBrowserRouter([
  {
    element: <AppLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "/dog-analyzer",
        element: (
          <ProtectedRoute>
            <DogAnalyzer />
          </ProtectedRoute>
        ),
      },

      {
        path: "/",
        element: <LandingPage />,
      },
      {
        path: "/privacy-policy",
        element: <PrivacyPolicy />,
      },
      {
        path: "/terms-of-service",
        element: <TermsOfService />,
      },
      {
        path: "/data-deletion",
        element: <DataDeletion />,
      },
      {
        path: "/success",
        element: <ProtectedRoute><Success /></ProtectedRoute>,
      },
      {
        path: "/failure",
        element: <ProtectedRoute><Failure /></ProtectedRoute>,
      },
      {
        path: "/overview",
        element: <ProtectedRoute> <AnalysisProtectedRoute><OverviewTab /> </AnalysisProtectedRoute></ProtectedRoute>,
      },
      {
        path: "/health",
        element: <ProtectedRoute> <AnalysisProtectedRoute><HealthTab /> </AnalysisProtectedRoute></ProtectedRoute>,
      },
      {
        path: "/care",
        element: <ProtectedRoute> <AnalysisProtectedRoute><CareTab /> </AnalysisProtectedRoute></ProtectedRoute>,
      },
      {
        path: "/characteristics",
        element: <ProtectedRoute> <AnalysisProtectedRoute><BreedCharacteristicsTab /> </AnalysisProtectedRoute></ProtectedRoute>,
      },
      {
        path: "/nutrition",
        element:
          <ProtectedRoute>
            <AnalysisProtectedRoute>
              <NutritionalTab />
            </AnalysisProtectedRoute>
          </ProtectedRoute>,
      },
      {
        path: "/historical",
        element: <ProtectedRoute><ReportHistorySection /></ProtectedRoute>,
      },
      {
        path: "/faqs",
        element: <ProtectedRoute><TipsGuide /></ProtectedRoute>,
      },
      {
        path: "*",
        element: (
          <ProtectedRoute>
            <DogAnalyzer />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

function App() {
  useEffect(() => {
    // Initial tracking of the current page
    trackRouteChange();

    // Listen for route changes by observing URL changes
    const handleRouteChange = () => {
      trackRouteChange();
    };

    // Listen for the popstate event (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <AnalysisProvider>
        <RouterProvider router={router} />
      </AnalysisProvider>
    </ThemeProvider>
  );
}

export default App;
